import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import HeaderMainPage, { newCampaign } from '../../header/HeaderMainPage';
import IconMail from '../../assets/icon_mail.svg';
import IconCampaign from '../../assets/icon_campaign.svg';
import IconHeart from '../../assets/icon_heart.svg';

/* =========================
   Global visual styles
   ========================= */
const GlobalStyles = () => (
  <style>{`
    @keyframes floaty { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
    @keyframes aurora { 0% { background-position: 0% 50% } 50% { background-position: 100% 50% } 100% { background-position: 0% 50% } }
    @keyframes pop { from { opacity: 0; transform: scale(.96) translateY(4px) } to { opacity: 1; transform: scale(1) translateY(0) } }
    .card-aurora::before { content: ''; position: absolute; inset: 0; padding: 1px; border-radius: 1rem;
      background: linear-gradient(120deg, rgba(99,102,241,.28), rgba(168,85,247,.28), rgba(16,185,129,.28));
      -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      -webkit-mask-composite: xor; mask-composite: exclude; animation: aurora 18s linear infinite; }
    .menu-pop { animation: pop .18s ease both; }
  `}</style>
);

/* =========================
   Light animated background
   ========================= */
const AnimatedBackground = () => (
  <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
    {/* soft light wash to match auth pages */}
    <div className="absolute inset-0 bg-gradient-to-b from-indigo-50 via-white to-fuchsia-50" />
    {/* dotted grid */}
    <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,rgba(2,6,23,.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,6,23,.06)_1px,transparent_1px)] [background-size:20px_20px]" />
    {/* glow blobs */}
    <div className="w-[36rem] h-[36rem] bg-gradient-to-tr from-indigo-400/20 via-fuchsia-400/20 to-emerald-400/20 rounded-full blur-3xl absolute -top-24 -right-24 animate-[floaty_12s_ease-in-out_infinite]" />
    <div className="w-[28rem] h-[28rem] bg-gradient-to-tr from-sky-400/20 via-indigo-400/20 to-purple-400/20 rounded-full blur-3xl absolute -bottom-20 -left-20 animate-[floaty_16s_ease-in-out_infinite]" />
  </div>
);

/* =========================
   Helpers (unchanged logic)
   ========================= */
const roboman_api = process.env.REACT_APP_ROBOMAN_API;

const Button = ({ onClick, className = '', text }) => (
  <button
    onClick={onClick}
    className={[
      'group inline-flex items-center gap-2 rounded-full px-5 py-2.5',
      'bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white',
      'shadow-[0_6px_20px_-6px_rgba(79,70,229,.35)] hover:shadow-[0_14px_34px_-10px_rgba(79,70,229,.45)]',
      'transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0',
      className,
    ].join(' ')}
  >
    {text}
  </button>
);

const getCampaignsStats = async (setCampaigns) => {
  try {
    const response = await fetch(`${roboman_api}/campaign/personal`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: localStorage.getItem('access_token'),
      },
    });
    if (!response.ok) console.error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    setCampaigns(data);
    return data.length;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    return null;
  }
};

const val = (obj, key) => (obj && typeof obj[key] === 'number' ? obj[key] : 0);

/* =========================
   Top statistic band
   ========================= */
const StatisticPanel = ({ campaignsData }) => {
  const navigate = useNavigate();
  const [emailStats, setEmailStats] = useState({ sent: 0, total: 0 });
  const [campaignsStats, setCampaignsStats] = useState(0);
  const [companyStats, setCompanyStats] = useState(0);
  const [campaigns, setCampaigns] = useState([]);

  const calculateEmailStats = (emailStatusCount) => {
    const esc = emailStatusCount || {};
    const total = Object.values(esc).reduce((acc, count) => acc + (Number(count) || 0), 0);
    return { sent: esc['sent'] || 0, total };
  };

  useEffect(() => {
    if (campaignsData?.email_status_count) {
      setEmailStats(calculateEmailStats(campaignsData.email_status_count));
    }
    if (campaignsData) {
      setCampaignsStats(campaignsData.num_of_campaigns || 0);
      setCompanyStats(campaignsData.num_of_companies || 0);
    }
  }, [campaignsData]);

  return (
    <div className="px-4 md:px-8 lg:px-12 pt-6 pb-4">
      {/* Page heading + CTA */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-600 mt-1">Quick pulse on your outreach performance</p>
        </div>

        <Button
          onClick={() => newCampaign(navigate)}
          text={
            <>
              <span className="text-lg leading-none">＋</span>
              <span className="font-medium">Create New Campaign</span>
            </>
          }
        />
      </div>

      {/* Thin brand rail to echo auth/header */}
      <div className="h-[2px] w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 mb-5" />

      {/* Stats cards — light glass */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Emails Sent */}
        <div className="relative overflow-hidden rounded-2xl p-5 text-slate-900 bg-white/80 backdrop-blur-sm ring-1 ring-slate-200 shadow-sm hover:shadow-md transition card-aurora">
          <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-indigo-500/10" />
          <div className="absolute -left-6 -bottom-10 h-24 w-24 rounded-full bg-fuchsia-500/10" />
          <div className="relative flex items-center">
            <img src={IconMail} alt="Email Icon" className="w-12 h-12 mr-4 drop-shadow-sm" />
            <div>
              <p className="text-sm text-slate-600">Emails Sent</p>
              <p className="text-3xl font-semibold tabular-nums">{`${emailStats.sent}/${emailStats.total}`}</p>
            </div>
          </div>
        </div>

        {/* Campaigns */}
        <div className="relative overflow-hidden rounded-2xl p-5 text-slate-900 bg-white/80 backdrop-blur-sm ring-1 ring-slate-200 shadow-sm hover:shadow-md transition card-aurora">
          <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-emerald-500/10" />
          <div className="absolute -left-6 -bottom-10 h-24 w-24 rounded-full bg-sky-500/10" />
          <div className="relative flex items-center">
            <img src={IconCampaign} alt="Campaign Icon" className="w-12 h-12 mr-4 drop-shadow-sm" />
            <div>
              <p className="text-sm text-slate-600">Campaigns</p>
              <p className="text-3xl font-semibold tabular-nums">{campaignsStats}</p>
            </div>
          </div>
        </div>

        {/* Companies */}
        <div className="relative overflow-hidden rounded-2xl p-5 text-slate-900 bg-white/80 backdrop-blur-sm ring-1 ring-slate-200 shadow-sm hover:shadow-md transition card-aurora">
          <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-violet-500/10" />
          <div className="absolute -left-6 -bottom-10 h-24 w-24 rounded-full bg-rose-500/10" />
          <div className="relative flex items-center">
            <img src={IconHeart} alt="Company Icon" className="w-12 h-12 mr-4 drop-shadow-sm" />
            <div>
              <p className="text-sm text-slate-600">Companies</p>
              <p className="text-3xl font-semibold tabular-nums">{companyStats}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================
   Doughnut charts section
   ========================= */
const DonutCharts = ({ sortBy, campaignsData }) => {
  const esc = campaignsData?.email_status_count || {};

  const progress = [
    { name: 'Sent', value: val(esc, 'sent') },
    { name: 'In Progress', value: (val(esc, 'reviewed') + val(esc, 'waiting to review')) },
    { name: 'Not Started', value: val(esc, 'not started') },
  ];

  const status = [
    { name: 'Replied', value: val(esc, 'replied') },
    { name: 'Read', value: val(esc, 'reviewed') },
    { name: 'Waiting to Review', value: val(esc, 'waiting to review') },
  ];

  const ProgressColor = ['#22d3ee', '#fb7185', '#3b82f6'];
  const CampaignStatusColor = ['#10b981', '#ef4444', '#f59e0b'];

  const charts = [
    { title: 'Progress', data: progress, colors: ProgressColor },
    { title: 'Campaign Status', data: status, colors: CampaignStatusColor },
  ];

  return (
    <div className="py-6 md:py-8 px-4 md:px-8 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {charts.map((chart, i) => (
          <div
            key={i}
            className="relative rounded-2xl border border-slate-200/70 bg-white/80 backdrop-blur-sm p-5 shadow-sm hover:shadow-md transition card-aurora"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">{chart.title}</h3>
            </div>

            <div className="relative">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={chart.data}
                    cx="50%"
                    cy="50%"
                    outerRadius="82%"
                    innerRadius="56%"
                    dataKey="value"
                    animationBegin={200}
                    animationDuration={800}
                  >
                    {chart.data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chart.colors[index % chart.colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '10px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 8px 24px rgba(15, 23, 42, .06)',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap justify-center gap-4">
              {chart.data.map((entry, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-slate-700">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: chart.colors[index % chart.colors.length] }}
                  />
                  <span>{entry.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* =========================
   Sort dropdown (unchanged)
   ========================= */
const SortByDropdown = ({ onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState('Date');

  const handleSort = (sort) => {
    setSelectedSort(sort);
    setIsOpen(false);
    onSortChange(sort.toLowerCase());
  };

  return (
    <div className="relative inline-block text-left w-44">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex justify-between items-center w-full rounded-xl border border-slate-300 px-4 py-2 bg-white text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
      >
        Sort by
        <svg
          className={`ml-2 h-4 w-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
          fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="origin-top-left absolute left-0 mt-2 w-full rounded-xl shadow-lg bg-white border border-slate-200 z-20 menu-pop">
          <div className="py-1" role="menu" aria-orientation="vertical">
            {['Date', 'Campaign name'].map((option) => (
              <button
                key={option}
                onClick={() => { handleSort(option); setIsOpen(false); }}
                className="block w-full px-4 py-2 text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 text-left"
                role="menuitem"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* =========================
   Charts wrapper
   ========================= */
const ChartPanel = ({ campaignsData }) => {
  const [sortBy, setSortBy] = useState('date');

  return (
    <div className="px-4 md:px-8 lg:px-12">
      <div className="mb-4 flex justify-start">
        <SortByDropdown onSortChange={setSortBy} />
      </div>
      <DonutCharts sortBy={sortBy} campaignsData={campaignsData} />
    </div>
  );
};

/* =========================
   Main body layout
   ========================= */
const MainBody = ({ campaignsData }) => (
  <div className="flex flex-col gap-2 flex-grow">
    <StatisticPanel campaignsData={campaignsData} />
    <ChartPanel campaignsData={campaignsData} />
  </div>
);

/* =========================
   Top-level Dashboard
   ========================= */
const DashboardPage = () => {
  const [campaignsData, setCampaignsData] = useState({
    num_of_campaigns: 0,
    num_of_companies: 0,
    email_status_count: {},
  });

  const fetchUserDataFromAPI = async (access_token) => {
    try {
      const response = await fetch(`${roboman_api}/users/me`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: access_token },
      });
      if (!response.ok) console.error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  const fetchCampaignsSummaryData = async (access_token) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/status/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: access_token },
      });
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching campaigns data:', error);
      return null;
    }
  };

  const fetchCampaignData = async () => {
    const access_token = localStorage.getItem('access_token');
    const userData = await fetchUserDataFromAPI(access_token);

    if (userData?.user_info?.nick_name) {
      localStorage.setItem('nickname', userData.user_info.nick_name);
      localStorage.setItem('havedata', JSON.stringify(true));
      if (
        userData.linkedin_connection_status === null ||
        userData.linkedin_connection_status === 'NOT_CONNECTED' ||
        userData.linkedin_connection_status === 'DISCONNECTED'
      ) {
        localStorage.setItem('lnk_act', JSON.stringify(false));
      } else {
        localStorage.setItem('lnk_act', JSON.stringify(true));
      }
    } else {
      localStorage.setItem('havedata', JSON.stringify(false));
      localStorage.setItem('lnk_act', JSON.stringify(false));
    }

    const campaignsDataSum = await fetchCampaignsSummaryData(access_token);
    if (campaignsDataSum) {
      setCampaignsData({
        num_of_campaigns: campaignsDataSum.num_of_campaigns,
        num_of_companies: campaignsDataSum.num_of_companies,
        email_status_count: campaignsDataSum.email_status_count,
      });
    }
  };

  useEffect(() => {
    fetchCampaignData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col bg-white">
      <GlobalStyles />
      <AnimatedBackground />

      {/* Keep your existing header for the app shell */}
      <HeaderMainPage />

      <main className="flex-1">
        <div className="mx-auto max-w-7xl w-full">
          <MainBody campaignsData={campaignsData} />
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
